/** @jsxImportSource preact */
import { h, render } from "preact";
import { useLayoutEffect, useRef, useState } from "preact/hooks";

import { readAppearance, type AppearanceSource } from "./appearance";
import { substituteProductTitle } from "./proactive/proactive";
import { WIDGET_KEYFRAMES } from "./ui/animations";
import type { WidgetChatMessage } from "./ui/message";
import { WidgetPanel } from "./ui/widget-panel";
import * as m from "./paraglide/messages.js";

/** 预览显示的主动提示：提示文案与预填问题的模板原文；{product_title} 换成示例商品名。 */
export interface PromptTemplates {
  /** 提示文案模板原文：去掉首尾空白后是空或没传时显示示例文字。 */
  promptTemplate?: string;
  /** 预填问题模板原文：没传或为空时输入框空着。 */
  questionTemplate?: string;
}

export interface WidgetPreviewOptions {
  /** 手机逻辑视口：展开占满盒子，关闭后显示底部胶囊。 */
  fill?: boolean;
  /** 只选择外观预览的起始视图，不触发主动规则。 */
  view?: "conversation" | "launcher" | "proactive";
  /** 预览显示的主动提示。 */
  promptTemplates?: PromptTemplates;
}

function previewProductText(template: string): string {
  return substituteProductTitle(template, m.widget_preview_product_title());
}

function previewPromptText(template: string | undefined): string {
  if (template === undefined || template.trim() === "") {
    return m.widget_preview_proactive();
  }
  return previewProductText(template);
}

/** 内容版本单调递增：输入框里的字只在写下它的版本里有效，内容改回原值也算变、打过的字不会复活。 */
function usePreviewPrompt({
  promptText,
  questionText,
  view,
}: {
  promptText: string;
  questionText: string;
  view: NonNullable<WidgetPreviewOptions["view"]>;
}) {
  const signature = JSON.stringify([promptText, questionText]);
  const contentVersion = useRef(0);
  const seenSignature = useRef(signature);
  if (seenSignature.current !== signature) {
    seenSignature.current = signature;
    contentVersion.current += 1;
  }
  const [consumedSignature, setConsumedSignature] = useState<string>();
  const [openedByPrompt, setOpenedByPrompt] = useState(false);
  const [draft, setDraft] = useState({ version: 0, value: "" });
  const messages: WidgetChatMessage[] = openedByPrompt
    ? [
        {
          id: "preview-prompt",
          role: "ai",
          text: promptText,
          textFormat: "plain",
        },
      ]
    : [];

  return {
    composerDraft: openedByPrompt
      ? draft.version === contentVersion.current
        ? draft.value
        : questionText
      : draft.value,
    messages,
    openedByPrompt,
    promptVisible: view === "proactive" && consumedSignature !== signature,
    activate: () => {
      setConsumedSignature(signature);
      setOpenedByPrompt(true);
      setDraft({ version: contentVersion.current, value: questionText });
    },
    dismiss: () => {
      setConsumedSignature(signature);
    },
    close: () => {
      setConsumedSignature(signature);
      setOpenedByPrompt(false);
      setDraft({ version: contentVersion.current, value: "" });
    },
    setComposerDraft: (value: string) => {
      setDraft({ version: contentVersion.current, value });
    },
  };
}

function PreviewPanel({
  settings,
  fill,
  view,
  promptTemplates,
}: {
  settings: AppearanceSource;
  fill: boolean;
  view: NonNullable<WidgetPreviewOptions["view"]>;
  promptTemplates?: PromptTemplates;
}) {
  const appearance = readAppearance(settings);
  const [open, setOpen] = useState(view === "conversation");
  const promptText = previewPromptText(promptTemplates?.promptTemplate);
  const questionText = previewProductText(
    promptTemplates?.questionTemplate ?? "",
  );
  const prompt = usePreviewPrompt({ promptText, questionText, view });
  const root = useRef<HTMLDivElement>(null);
  const closing = useRef(false);

  useLayoutEffect(() => {
    if (!open && closing.current) {
      root.current
        ?.querySelector<HTMLButtonElement>(".gn-launcher button")
        ?.focus();
      closing.current = false;
    }
  }, [open, prompt.promptVisible]);

  return (
    <div
      ref={root}
      style={{
        display: "flex",
        justifyContent:
          appearance.position === "left" ? "flex-start" : "flex-end",
        height: fill && open ? "100%" : undefined,
        ...(fill && !open
          ? { position: "absolute", bottom: 16, left: 16, right: 16 }
          : {}),
      }}
    >
      <style>{WIDGET_KEYFRAMES}</style>
      <WidgetPanel
        proactivePromptStyle={appearance.proactivePromptStyle}
        accent={appearance.accent}
        avatar={appearance.avatar}
        blur={appearance.blur}
        character={appearance.character}
        capsule={
          open
            ? { size: "large" }
            : prompt.promptVisible
              ? {
                  size: "medium",
                  prompt: {
                    text: promptText,
                    activate: () => {
                      prompt.activate();
                      setOpen(true);
                    },
                    dismiss: () => {
                      closing.current = true;
                      prompt.dismiss();
                    },
                  },
                }
              : { size: "small" }
        }
        direction={appearance.direction}
        draft={prompt.composerDraft}
        empty={!prompt.openedByPrompt}
        emptyTitle={appearance.welcomeMessage}
        fill={fill && open}
        font={appearance.font}
        fontSize={appearance.fontSize}
        messages={prompt.messages}
        onClose={() => {
          closing.current = true;
          prompt.close();
          setOpen(false);
        }}
        onDraftChange={prompt.setComposerDraft}
        onOpen={() => {
          setOpen(true);
        }}
        position={appearance.position}
        radius={appearance.radius}
        reserveResizeSpace={!fill}
        suggestions={appearance.suggestions}
        surface={appearance.surface}
        surfaceOpacity={appearance.surfaceOpacity}
        title={appearance.title}
        width={fill ? "100%" : undefined}
      />
    </div>
  );
}

/**
 * 把面板单独挂到宿主容器上，给后台 Settings 的预览用：不握手、不连 socket、不建会话。
 * 同一视图重复调用只更新草稿并保留开合状态；切换视图时重置到该视图。调用方负责缩放整个预览场景，不改面板尺寸或阴影。
 * Shadow DOM 隔离后台样式；跟随店铺字体时只继承当前宿主的字体。
 */
export function mountWidgetPreview(
  container: HTMLElement,
  settings: AppearanceSource,
  {
    fill = false,
    view = "conversation",
    promptTemplates,
  }: WidgetPreviewOptions = {},
): () => void {
  const shadowRoot =
    container.shadowRoot ?? container.attachShadow({ mode: "open" });

  render(
    h(PreviewPanel, {
      key: view,
      settings,
      fill,
      view,
      promptTemplates,
    }),
    shadowRoot,
  );

  return () => {
    render(null, shadowRoot);
  };
}
